import { useMemo } from 'react';

export type FriendlyDatetime = {
  formatStart: (iso?: string | null, options?: { now?: Date }) => string | null;
  formatEta: (iso?: string | null, options?: { now?: Date }) => string | null;
};

function isValidDate(d: Date): boolean {
  return Number.isFinite(d.getTime());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isTomorrow(d: Date, now: Date): boolean {
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return isSameDay(d, tomorrow);
}

function formatTime(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

function formatAbsolute(d: Date, now: Date, locale: string): string {
  const base: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  const includeYear = d.getFullYear() !== now.getFullYear();
  const opts: Intl.DateTimeFormatOptions = includeYear
    ? { ...base, year: 'numeric' }
    : base;
  return new Intl.DateTimeFormat(locale, opts).format(d);
}

function formatDuration(deltaMs: number): string {
  const ms = Math.max(0, deltaMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (ms < minute) return 'in less than a minute';

  const d = Math.floor(ms / day);
  const h = Math.floor((ms % day) / hour);
  const m = Math.floor((ms % hour) / minute);

  if (d >= 7) return '';

  if (d > 0 && h > 0)
    return `in ${d} day${d === 1 ? '' : 's'} ${h} hour${h === 1 ? '' : 's'}`;
  if (d > 0) return `in ${d} day${d === 1 ? '' : 's'}`;
  if (h > 0 && m > 0) return `in ${h} hour${h === 1 ? '' : 's'} ${m} min`;
  if (h > 0) return `in ${h} hour${h === 1 ? '' : 's'}`;
  return `in ${m} min`;
}

export function useFriendlyDatetime(): FriendlyDatetime {
  const locale = useMemo<string>(() => {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }, []);

  const formatStart: FriendlyDatetime['formatStart'] = (
    iso?: string | null,
    options?: { now?: Date }
  ) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (!isValidDate(d)) return iso;
    const now = options?.now ?? new Date();

    if (isSameDay(d, now)) return `today at ${formatTime(d, locale)}`;
    if (isTomorrow(d, now)) return `tomorrow at ${formatTime(d, locale)}`;
    return `on ${formatAbsolute(d, now, locale)}`;
  };

  const formatEta: FriendlyDatetime['formatEta'] = (
    iso?: string | null,
    options?: { now?: Date }
  ) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (!isValidDate(d)) return iso;
    const now = options?.now ?? new Date();
    const delta = d.getTime() - now.getTime();

    const rel = formatDuration(delta);
    if (rel) return rel;
    // Fallback to absolute if too far out (> 7 days)
    return `on ${formatAbsolute(d, now, locale)}`;
  };

  return { formatStart, formatEta };
}

export default useFriendlyDatetime;
